import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useOrgJwt from '../hooks/useOrgJwt';
import cfg from '../config/billing';
import './BillingPortal.css';

const L = (...a) => console.log('[BillingPortal]', ...a);

const apiBase = process.env.REACT_APP_API_BASE_URL || '';

// Map tier keys to price IDs from config (same as Pricing.jsx)
const priceIdMap = {
  free: cfg.priceFree,
  pro: cfg.pricePro,
  enterprise: cfg.priceEnterprise,
};

// Create Stripe checkout session for free→paid upgrades
async function createCheckoutSession(jwt, priceId) {
  const origin = window.location.origin;
  const res = await fetch(`${apiBase}/billing/create-checkout-session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      PriceId: priceId,
      SuccessUrl: `${origin}/iqledger/dashboard?billing=success`,
      CancelUrl: `${origin}/billing/change-plan?billing=cancel`,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to create checkout session');
  }
  const data = await res.json();
  return data.url;
}

async function fetchBillingMe(jwt) {
  const res = await fetch(`${apiBase}/billing/me`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!res.ok) throw new Error('Failed to fetch billing');
  return res.json();
}

async function fetchPlans(jwt) {
  const res = await fetch(`${apiBase}/billing/plans`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!res.ok) throw new Error('Failed to fetch plans');
  return res.json();
}

async function changePlan(jwt, priceId) {
  const res = await fetch(`${apiBase}/billing/change-plan`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ PriceId: priceId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to change plan');
  }
  return res.json();
}

function formatTokens(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}k`;
  return String(n);
}

export default function BillingPortal() {
  const navigate = useNavigate();
  const { orgJwt, loading: jwtLoading, role } = useOrgJwt();

  const [state, setState] = useState({
    loading: true,
    plans: [],
    currentTier: 'free',
    currentStatus: 'none',
    changingTo: null,
    error: '',
    success: '',
    // Modal state
    showModal: false,
    modalPlan: null,
    modalType: null, // 'upgrade' or 'downgrade'
  });

  const isAdmin = role === 'admin';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (jwtLoading) return;
      if (!orgJwt) {
        setState(s => ({ ...s, loading: false, error: 'No organization token.' }));
        return;
      }
      try {
        const [meRes, plansRes] = await Promise.all([
          fetchBillingMe(orgJwt),
          fetchPlans(orgJwt),
        ]);
        if (cancelled) return;
        const sub = meRes?.subscription || {};
        // Add priceId from config to each plan
        const plansWithPriceIds = (plansRes.plans || []).map(plan => ({
          ...plan,
          priceId: priceIdMap[plan.key] || plan.priceId,
        }));
        setState(s => ({
          ...s,
          loading: false,
          plans: plansWithPriceIds,
          currentTier: (sub.tier || 'free').toLowerCase(),
          currentStatus: (sub.status || 'none').toLowerCase(),
        }));
      } catch (e) {
        L('Load error:', e);
        if (!cancelled) setState(s => ({ ...s, loading: false, error: 'Failed to load billing data.' }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [jwtLoading, orgJwt]);

  const getPlanRank = (key) => {
    const ranks = { free: 0, pro: 1, enterprise: 2 };
    return ranks[key] ?? 0;
  };

  const currentPlanRank = useMemo(() => getPlanRank(state.currentTier), [state.currentTier]);

  // Handle clicking on a plan - decide whether to show modal or go to checkout
  const handleChangePlan = async (plan) => {
    if (!isAdmin) {
      setState(s => ({ ...s, error: 'Only admins can change the plan.' }));
      return;
    }
    if (!plan.priceId) {
      setState(s => ({ ...s, error: 'Price ID not configured for this plan.' }));
      return;
    }

    const isUpgradingFromFree = state.currentTier === 'free' && getPlanRank(plan.key) > 0;
    const isDowngrade = getPlanRank(plan.key) < getPlanRank(state.currentTier);
    const isPaidToPaidUpgrade = !isUpgradingFromFree && getPlanRank(plan.key) > getPlanRank(state.currentTier);

    if (isUpgradingFromFree) {
      // Free → Paid: Go directly to Stripe checkout (no payment method on file)
      setState(s => ({ ...s, changingTo: plan.key, error: '', success: '' }));
      try {
        const checkoutUrl = await createCheckoutSession(orgJwt, plan.priceId);
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          throw new Error('No checkout URL returned');
        }
      } catch (e) {
        L('Checkout error:', e);
        setState(s => ({
          ...s,
          changingTo: null,
          error: e.message || 'Failed to start checkout. Please try again.',
        }));
      }
      return;
    }

    // Paid → Paid or Downgrade: Show confirmation modal
    setState(s => ({
      ...s,
      showModal: true,
      modalPlan: plan,
      modalType: isDowngrade ? 'downgrade' : 'upgrade',
      error: '',
    }));
  };

  // Close the modal without action
  const handleCloseModal = () => {
    setState(s => ({ ...s, showModal: false, modalPlan: null, modalType: null }));
  };

  // Confirm the plan change from the modal
  const handleConfirmChange = async () => {
    const plan = state.modalPlan;
    if (!plan) return;

    setState(s => ({ ...s, showModal: false, changingTo: plan.key, error: '', success: '' }));

    try {
      const result = await changePlan(orgJwt, plan.priceId);
      L('Plan changed:', result);
      setState(s => ({
        ...s,
        changingTo: null,
        modalPlan: null,
        modalType: null,
        currentTier: plan.key,
        success: result.message || `Successfully changed to ${plan.name} plan!`,
      }));
      // Redirect after short delay
      setTimeout(() => navigate('/iqledger/settings'), 2000);
    } catch (e) {
      L('Change error:', e);
      setState(s => ({
        ...s,
        changingTo: null,
        modalPlan: null,
        modalType: null,
        error: e.message || 'Failed to change plan. Please try again.',
      }));
    }
  };

  if (state.loading || jwtLoading) {
    return (
      <div className="billing-portal-wrap">
        <div className="billing-portal-loading">Loading plans...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="billing-portal-wrap">
        <div className="billing-portal-header">
          <button className="back-btn" onClick={() => navigate('/settings')}>← Back to Settings</button>
          <h1>Change Plan</h1>
        </div>
        <div className="billing-portal-error">
          Only organization admins can change the subscription plan.
        </div>
      </div>
    );
  }

  return (
    <div className="billing-portal-wrap">
      <div className="billing-portal-header">
        <button className="back-btn" onClick={() => navigate('/settings')}>← Back to Settings</button>
        <h1>Change Plan</h1>
        <p className="subtitle">Select a plan to upgrade or downgrade your subscription</p>
      </div>

      {state.error && <div className="billing-portal-error">{state.error}</div>}
      {state.success && <div className="billing-portal-success">{state.success}</div>}

      <div className="plans-grid">
        {state.plans.map((plan) => {
          const isCurrent = plan.key === state.currentTier;
          const planRank = getPlanRank(plan.key);
          const isUpgrade = planRank > currentPlanRank;
          const isDowngrade = planRank < currentPlanRank;
          const isChanging = state.changingTo === plan.key;

          return (
            <div
              key={plan.key}
              className={`plan-card ${plan.key} ${isCurrent ? 'current' : ''} ${plan.key === 'pro' ? 'popular' : ''}`}
            >
              {plan.key === 'pro' && <span className="popular-badge">Most Popular</span>}
              {isCurrent && <span className="current-badge">Current Plan</span>}

              <div className="plan-name">{plan.name}</div>
              <div className="plan-price">{plan.priceDisplay}</div>
              <div className="plan-tokens">{formatTokens(plan.tokenCap)} tokens/mo</div>

              <ul className="plan-features">
                {plan.features.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>

              <button
                className={`plan-btn ${isCurrent ? 'current' : isUpgrade ? 'upgrade' : 'downgrade'}`}
                disabled={isCurrent || isChanging || !!state.changingTo}
                onClick={() => handleChangePlan(plan)}
              >
                {isChanging
                  ? 'Processing...'
                  : isCurrent
                  ? 'Current Plan'
                  : isUpgrade
                  ? `Upgrade to ${plan.name}`
                  : `Downgrade to ${plan.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="billing-portal-footer">
        <p>Need to update payment method or view invoices?</p>
        <button className="link-btn" onClick={() => navigate('/iqledger/settings')}>
          Go to Settings
        </button>
      </div>

      {/* Confirmation Modal */}
      {state.showModal && state.modalPlan && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>
              {state.modalType === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
            </h2>
            
            {state.modalType === 'upgrade' ? (
              <div className="modal-body">
                <p>
                  You are about to upgrade to the <strong>{state.modalPlan.name}</strong> plan.
                </p>
                <div className="modal-warning">
                  <span className="warning-icon">⚠️</span>
                  <p>
                    Your payment method on file will be charged the prorated difference immediately.
                  </p>
                </div>
              </div>
            ) : (
              <div className="modal-body">
                <p>
                  You are about to downgrade to the <strong>{state.modalPlan.name}</strong> plan.
                </p>
                <p className="modal-note">
                  Your new limits will take effect at the end of your current billing period.
                </p>
              </div>
            )}

            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                className={`modal-btn confirm ${state.modalType}`}
                onClick={handleConfirmChange}
              >
                {state.modalType === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
